import { useMutation, UseMutationOptions } from "@tanstack/react-query";
import { ExtendTimeDocument, ExtendTimeMutation, ExtendTimeMutationVariables } from "../../generatedQueries";
import request from "graphql-request";
import { getGraphqlEndpoint } from "../../config/config";
import { backoffRetry, maybeRetryMutation } from "../../lib/utils";
import { Auth } from "../../auth/core/Auth";
import { event } from "../../events/core/helpers";
import { ExtendTimeRequest } from "../../events/event-types/parkable-transactions/ExtendTimeRequest";
import { ExtendTimeResponse } from "../../events/event-types/parkable-transactions/ExtendTimeResponse";
import { getDeviceHeaders } from "../../http/getDeviceHeaders/getDeviceHeaders";

type Options = Omit<
  UseMutationOptions<
    ExtendTimeMutation["extendTime"],
    unknown,
    ExtendTimeMutationVariables
  >,
  "mutationFn"
>;

export default function useExtendTime(options: Options) {
  const mutation = useMutation<
    ExtendTimeMutation["extendTime"],
    unknown,
    ExtendTimeMutationVariables
  >(
    async (variables) => {
      const jwt = (await Auth.getInstance().getSession())?.session.idToken;
      const deviceHeaders = getDeviceHeaders();
      const requestHeaders = jwt ? {
        ...deviceHeaders,
        Authorization: `Bearer ${jwt}`,
      } : { ...deviceHeaders };
      
      return (await request({
        url: getGraphqlEndpoint() as string,
        document: ExtendTimeDocument,
        variables: {
          ...variables,
        },
        requestHeaders,
        // signal,
      })).extendTime;
    },
    {
      retry: (failureCount: number, error: unknown) =>
        maybeRetryMutation(failureCount, error, 3),
      retryDelay: (failureAttempt: number) =>
        backoffRetry(failureAttempt, 30 * 1000),

      ...options,

      onMutate: (variables) => {
        event(
          new ExtendTimeRequest({
            mutationVariables: variables,
          })
        );
      },
      onSuccess: (response, variables, context) => {
        event(
          new ExtendTimeResponse({
            mutationResponse: response,
          })
        );

        const parkableTransaction = response?.parkableTransaction;

        if (options.onSuccess) {
          options.onSuccess(response, variables, context);
        }
      },
    }
  );

  return {
    mutation,
    parkableTransaction: mutation.data ?? null,
  };
}
